/**
 * TribunalReady root shell.
 *
 * This module is intentionally TINY and free of any auth/store/Cognito imports.
 * Its only job is to branch at the very top of the tree:
 *
 *   /r/{token}      ->  PublicReportPage       (tenant maintenance intake, NO auth)
 *   /apply/{token}  ->  PublicApplicationPage  (rental application, NO auth)
 *   everything      ->  AuthedApp              (the full Cognito-gated app)
 *
 * ALL targets are lazy-loaded via dynamic import(), so the worlds land in
 * separate bundles. A tenant hitting /r/{token} — or an applicant hitting
 * /apply/{token} — downloads only the public chunk; authStore, dataApi, and
 * amazon-cognito-identity-js never load on their phone. The pathname checks
 * MUST stay here, above AuthedApp, so we return before AuthedApp (and
 * therefore checkSession / the auth gate / store hydration) is ever imported.
 */

import React, { lazy, Suspense } from 'react';
// ErrorBoundary is pure (React only) — safe to import on the public path.
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';

const AuthedApp = lazy(() => import('./AuthedApp'));
const PublicReportPage = lazy(() => import('@/pages/PublicReportPage'));
const PublicApplicationPage = lazy(() => import('@/pages/PublicApplicationPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));

// Minimal, dependency-free splash for the chunk fetch (no store-backed chrome).
const Splash: React.FC = () => <div className="min-h-screen bg-slate-950" aria-busy="true" />;

const App: React.FC = () => {
  const path = window.location.pathname;

  // PUBLIC TENANT INTAKE — must short-circuit before AuthedApp loads.
  if (path.startsWith('/r/')) {
    const token = decodeURIComponent(path.slice(3));
    return (
      <ErrorBoundary>
        <Suspense fallback={<Splash />}>
          <PublicReportPage token={token} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // PUBLIC RENTAL APPLICATION — one link per unit, must also short-circuit.
  if (path.startsWith('/apply/')) {
    const token = decodeURIComponent(path.slice('/apply/'.length));
    return (
      <ErrorBoundary>
        <Suspense fallback={<Splash />}>
          <PublicApplicationPage token={token} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // PUBLIC PRIVACY POLICY — no auth (linked from Settings + Plaid diligence).
  if (path === '/privacy' || path === '/privacy/') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Splash />}>
          <PrivacyPolicyPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // AUTHENTICATED APP — lazy so its Cognito/store graph stays off the /r/ path.
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('[App] Critical error caught by boundary:', {
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        });
      }}
    >
      <Suspense fallback={<Splash />}>
        <AuthedApp />
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
